import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Heart, Users, Lightbulb, ArrowRight, ChevronDown, ArrowUp } from "lucide-react";
import { useState, useEffect } from "react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { HreflangTags } from "@/components/HreflangTags";

export default function AboutPage() {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <Helmet>
        <title>Ranvir Gill - Founder of CheckFirst | Healthcare Innovator & AI Pioneer</title>
        <meta
          name="description"
          content="Meet Ranvir Gill, founder and CEO of CheckFirst. Healthcare entrepreneur revolutionizing patient care with AI algorithm technology. Learn about Ranvir Gill's mission to reduce unnecessary ER visits and improve healthcare access."
        />
        <meta
          name="keywords"
          content="Ranvir Gill, Ranvir Gill founder, Ranvir Gill CheckFirst, Ranvir Gill healthcare, Ranvir Gill CEO, CheckFirst founder, healthcare entrepreneur, AI healthcare founder, healthcare innovator, AI algorithm healthcare, digital health founder, healthtech entrepreneur"
        />
        <meta name="author" content="Ranvir Gill" />

        {/* Open Graph for social sharing */}
        <meta property="og:title" content="Ranvir Gill - Founder of CheckFirst | Healthcare Innovator" />
        <meta
          property="og:description"
          content="Meet Ranvir Gill, founder and CEO of CheckFirst. Healthcare entrepreneur revolutionizing patient care with AI algorithm technology to reduce unnecessary ER visits."
        />
        <meta property="og:url" content="https://www.checkfirstcare.com/about" />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content="https://www.checkfirstcare.com/og-about-founder.jpg" />
        <meta property="og:image:alt" content="Ranvir Gill - Founder and CEO of CheckFirst" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="profile:first_name" content="Ranvir" />
        <meta property="profile:last_name" content="Gill" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ranvir Gill - Founder of CheckFirst | Healthcare Innovator" />
        <meta
          name="twitter:description"
          content="Meet Ranvir Gill, founder and CEO of CheckFirst. Healthcare entrepreneur revolutionizing patient care with AI algorithm technology."
        />
        <meta name="twitter:image" content="https://www.checkfirstcare.com/og-about-founder.jpg" />
        <meta name="twitter:image:alt" content="Ranvir Gill - Founder and CEO of CheckFirst" />

        <link rel="canonical" href="https://www.checkfirstcare.com/about" />

        {/* JSON-LD Structured Data for Person (Ranvir Gill) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Ranvir Gill",
            "image": "https://www.checkfirstcare.com/ranvir-gill.jpg",
            "jobTitle": "Founder & CEO",
            "worksFor": {
              "@type": "Organization",
              "name": "CheckFirst",
              "url": "https://www.checkfirstcare.com",
              "description": "AI-powered patient triage platform revolutionizing healthcare access",
              "founder": {
                "@type": "Person",
                "name": "Ranvir Gill"
              }
            },
            "description": "Healthcare entrepreneur and founder of CheckFirst, an AI-powered patient triage platform. Ranvir Gill is dedicated to reducing unnecessary ER visits and improving healthcare accessibility through innovative technology.",
            "knowsAbout": [
              "Healthcare Technology",
              "AI in Healthcare",
              "Patient Triage Systems",
              "Digital Health Innovation",
              "Healthcare Accessibility",
              "Emergency Room Optimization"
            ],
            "sameAs": [
              "https://www.linkedin.com/in/ranvir-gill-6894b7199",
              "https://twitter.com/checkfirstcare"
            ]
          })}
        </script>

        {/* JSON-LD for Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "CheckFirst",
            "url": "https://www.checkfirstcare.com",
            "logo": "https://www.checkfirstcare.com/logo.png",
            "founder": {
              "@type": "Person",
              "name": "Ranvir Gill",
              "jobTitle": "Founder & CEO"
            },
            "description": "CheckFirst is an AI-powered healthcare triage platform founded by Ranvir Gill to reduce unnecessary emergency room visits and improve patient outcomes.",
            "foundingDate": "2024",
            "areaServed": "United States",
            "serviceType": "Healthcare Technology"
          })}
        </script>

        {/* JSON-LD for AboutPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Ranvir Gill and CheckFirst",
            "description": "Learn about Ranvir Gill, founder of CheckFirst, and his mission to transform healthcare through AI-powered patient triage technology.",
            "mainEntity": {
              "@type": "Person",
              "name": "Ranvir Gill",
              "jobTitle": "Founder & CEO",
              "description": "Healthcare entrepreneur dedicated to reducing unnecessary ER visits through intelligent triage technology"
            }
          })}
        </script>

        {/* JSON-LD for BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.checkfirstcare.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "About",
                "item": "https://www.checkfirstcare.com/about"
              }
            ]
          })}
        </script>
      </Helmet>
      <HreflangTags />

      <div className="min-h-screen">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 lg:px-8 pt-6">
          <Breadcrumbs items={[{ label: 'About' }]} />
        </div>

        {/* Hero Section */}
        <section className="py-12 lg:py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h1 className="text-4xl lg:text-6xl font-bold text-foreground leading-tight">
                About <span className="text-primary">CheckFirst</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Prevent Non-Emergency Hospitalization through Intelligent Triage Technology.
              </p>
            </div>
          </div>
        </section>

        {/* Animated Arrow */}
        <div className="flex justify-center -mt-6 mb-6 relative z-10">
          <button
            onClick={() => {
              document.getElementById('founders-story')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }}
            className="animate-bounce animate-pulse-glow cursor-pointer hover:scale-110 transition-transform"
            aria-label="Scroll to Founder's Story"
          >
            <ChevronDown className="h-12 w-12 text-primary" strokeWidth={3} />
          </button>
        </div>

        {/* Founder's Story */}
        <section id="founders-story" className="py-8 lg:py-16 bg-secondary/50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <Card className="shadow-card">
                <CardContent className="p-8 lg:p-12">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="space-y-6">
                      <div className="w-16 h-16 gradient-hero rounded-2xl flex items-center justify-center">
                        <Lightbulb className="h-8 w-8 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-foreground">
                        Ranvir Gill: The Spark for Innovation
                      </h2>
                      <div className="space-y-4 text-muted-foreground">
                        <p>
                          <strong>My name is Ranvir Gill</strong>, and I am the founder and CEO of CheckFirst. The idea for this company came to me during a particularly
                          challenging period in healthcare. I witnessed firsthand how overwhelmed emergency departments
                          were becoming, with patients unsure of where to seek appropriate care.
                        </p>
                        <p>
                          As a healthcare entrepreneur, the breaking point came in 2025 when Crozer Hospital shut down 
                          and wait times at remaining facilities became critical. Meanwhile, urban ERs were flooded with
                          non-emergency cases that could have been better served elsewhere.
                        </p>
                        <p>
                          I realized that if we could intelligently guide patients to the right level of care from the
                          beginning, we could reduce unnecessary ER visits, save costs, and ultimately help keep
                          hospitals operational while improving patient outcomes. This became my mission as the founder of CheckFirst.
                        </p>
                      </div>
                    </div>
                    <div className="bg-gradient-card rounded-2xl p-8 text-center">
                      <div className="text-6xl font-bold text-primary mb-2">150+</div>
                      <div className="text-muted-foreground mb-4">Rural Hospitals Closed</div>
                      <div className="text-sm text-muted-foreground">
                        Since 2010, highlighting the urgent need for healthcare innovation
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Animated Arrow */}
        <div className="flex justify-center -mt-6 mb-6 relative z-10">
          <button
            onClick={() => {
              document.getElementById('mission-vision')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }}
            className="animate-bounce animate-pulse-glow cursor-pointer hover:scale-110 transition-transform"
            aria-label="Scroll to Mission & Vision"
          >
            <ChevronDown className="h-12 w-12 text-primary" strokeWidth={3} />
          </button>
        </div>

        {/* Mission & Vision */}
        <section id="mission-vision" className="py-8 lg:py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Mission */}
              <Card className="shadow-card hover:shadow-card-hover transition-smooth">
                <CardContent className="p-8 text-center space-y-6">
                  <div className="w-16 h-16 mx-auto gradient-hero rounded-2xl flex items-center justify-center">
                    <Heart className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Our Mission</h3>
                  <p className="text-muted-foreground leading-relaxed text-justify">
                    At CheckFirst, our mission is to revolutionize healthcare access through AI-driven triage solutions that connect patients to the right care at the right time. We strive to reduce unnecessary ER visits, lower healthcare costs, and empower providers with real-time, data-driven insights that enhance efficiency, improve outcomes, and make quality care equitable for all.
                  </p>
                </CardContent>
              </Card>

              {/* Vision */}
              <Card className="shadow-card hover:shadow-card-hover transition-smooth">
                <CardContent className="p-8 text-center space-y-6">
                  <div className="w-16 h-16 mx-auto gradient-success rounded-2xl flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Our Vision</h3>
                  <p className="text-muted-foreground leading-relaxed text-justify">
                    At CheckFirst, we believe that everyone deserves care that truly fits their needs. Our vision is to make healthcare more accessible, fair, and compassionate by helping people choose the right care at the right time. By preventing unnecessary hospital visits, we're working to shorten wait times, ease the burden on emergency rooms, and ensure that every community can get help when it matters most. Because healthcare isn't a privilege. It's a human right.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Animated Arrow */}
        <div className="flex justify-center -mt-6 mb-6 relative z-10">
          <button
            onClick={() => {
              document.getElementById('healthcare-crisis')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }}
            className="animate-bounce animate-pulse-glow cursor-pointer hover:scale-110 transition-transform"
            aria-label="Scroll to Healthcare Crisis"
          >
            <ChevronDown className="h-12 w-12 text-primary" strokeWidth={3} />
          </button>
        </div>

        {/* The Problem We're Solving */}
        <section id="healthcare-crisis" className="py-8 lg:py-16 bg-secondary/50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center space-y-4 mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
                  The Healthcare Crisis We're Addressing
                </h2>
                <p className="text-xl text-muted-foreground">
                  Understanding the urgent need for intelligent healthcare triage
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center space-y-4">
                  <div className="text-4xl font-bold text-white">74%</div>
                  <div className="text-muted-foreground">of ER visits could be handled elsewhere</div>
                </div>
                <div className="text-center space-y-4">
                  <div className="text-4xl font-bold text-white">$4.4B</div>
                  <div className="text-muted-foreground">wasted annually on inappropriate ER visits</div>
                </div>
                <div className="text-center space-y-4">
                  <div className="text-4xl font-bold text-white">4 hours</div>
                  <div className="text-muted-foreground">average ER wait time for non-emergency cases</div>
                </div>
              </div>

              <Card className="shadow-card">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-foreground mb-4">The Vicious Cycle</h3>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      <strong>1. Uncertainty:</strong> Patients unsure about their symptoms default to the ER for
                      "safety"
                    </p>
                    <p>
                      <strong>2. Overcrowding:</strong> ERs become overwhelmed with non-emergency cases
                    </p>
                    <p>
                      <strong>3. Resource Strain:</strong> Hospitals struggle financially and operationally
                    </p>
                    <p>
                      <strong>4. Closures:</strong> Rural and community hospitals shut down, reducing access
                    </p>
                    <p>
                      <strong>5. Worse Outcomes:</strong> Patients have less access to care, cycle continues
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Animated Arrow */}
        <div className="flex justify-center -mt-6 mb-6 relative z-10">
          <button
            onClick={() => {
              document.getElementById('our-solution')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }}
            className="animate-bounce animate-pulse-glow cursor-pointer hover:scale-110 transition-transform"
            aria-label="Scroll to Our Solution"
          >
            <ChevronDown className="h-12 w-12 text-primary" strokeWidth={3} />
          </button>
        </div>

        {/* Our Solution */}
        <section id="our-solution" className="py-8 lg:py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground">How We're Breaking the Cycle</h2>
              <p className="text-xl text-muted-foreground">
                CheckFirst uses advanced AI to provide intelligent healthcare triage, guiding patients to the most
                appropriate level of care from the start.
              </p>

              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <Card className="shadow-card text-left">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-3">For Patients</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      <li>• Instant, 24/7 symptom assessment</li>
                      <li>• Clear guidance on next steps</li>
                      <li>• Reduced anxiety and uncertainty</li>
                      <li>• Better health outcomes</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="shadow-card text-left">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-3">For Healthcare Systems</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      <li>• Reduced non-emergency ER visits</li>
                      <li>• Lower operational costs</li>
                      <li>• Improved resource allocation</li>
                      <li>• Sustainable healthcare delivery</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <Button variant="hero" size="lg" asChild>
                <Link to="/triage">
                  Try Our AI Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Animated Arrow */}
        <div className="flex justify-center -mt-6 mb-6 relative z-10">
          <button
            onClick={() => {
              document.getElementById('core-values')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }}
            className="animate-bounce animate-pulse-glow cursor-pointer hover:scale-110 transition-transform"
            aria-label="Scroll to Core Values"
          >
            <ChevronDown className="h-12 w-12 text-primary" strokeWidth={3} />
          </button>
        </div>

        {/* Values */}
        <section id="core-values" className="py-8 lg:py-16 gradient-hero">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12">Our Core Values</h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">Accuracy</h3>
                  <p className="text-white/90 text-sm">
                    Evidence-based AI recommendations grounded in medical best practices
                  </p>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">Accessibility</h3>
                  <p className="text-white/90 text-sm">Healthcare guidance available to everyone, anytime, anywhere</p>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">Trust</h3>
                  <p className="text-white/90 text-sm">
                    HIPAA-compliant platform you can trust with your health information
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our 2025 Impact */}
        <section id="impact-2025" className="py-8 lg:py-16 bg-secondary/50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground">Our 2025 Impact</h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                <Card className="shadow-card text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-primary mb-2">4,000+</div>
                    <div className="text-muted-foreground text-sm">Patients in the tri-state area</div>
                  </CardContent>
                </Card>
                <Card className="shadow-card text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-primary mb-2">10,776</div>
                    <div className="text-muted-foreground text-sm">Billable hours saved</div>
                  </CardContent>
                </Card>
                <Card className="shadow-card text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-primary mb-2">$232K</div>
                    <div className="text-muted-foreground text-sm">Estimated revenue saved</div>
                  </CardContent>
                </Card>
                <Card className="shadow-card text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-primary mb-2">39%</div>
                    <div className="text-muted-foreground text-sm">Reduction in unnecessary ER visits</div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 z-50 bg-primary hover:bg-primary/90 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in hover:scale-110"
            aria-label="Back to top"
          >
            <ArrowUp className="h-6 w-6" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </>
  );
}
